import { NgClass } from '@angular/common';
import { Component, HostBinding, OnInit } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterOutlet,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { UserStoreService } from '@polaris-customerweb/sharedcomponents.core';
import { UserProfileService } from '@polaris-customerweb/sharedcomponents.user-profile';
import { firstValueFrom } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { changeSidenavState } from './components/mobile-sidenav/actions/sidenav.actions';
import { MobileSidenavComponent } from './components/mobile-sidenav/mobile-sidenav.component';
import { AppState } from './models/app-state';
import { RideTypeService } from './services/ride-type.service';
import { AnalyticsService } from './state/analytics/analytics.service';
import { UserAuthService } from './state/user-auth/user-auth.service';

@Component({
    imports: [NgClass, MatSidenavModule, MobileSidenavComponent, RouterOutlet],
    selector: 'rdx-root',
    standalone: true,
    styleUrls: ['./app.component.scss'],
    templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
    static readonly className = 'AppComponent';

    @HostBinding('className') epiClassName: string;

    isSafari: boolean;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly analyticsService: AnalyticsService,
        private readonly configService: ConfigService,
        private readonly profileService: UserProfileService,
        private readonly rideTypeService: RideTypeService,
        private readonly router: Router,
        private readonly store: Store<AppState>,
        private readonly titleService: Title,
        readonly userAuthService: UserAuthService,
        private readonly userStoreService: UserStoreService
    ) {
        this.epiClassName = this.configService.isEpi ? 'isEpi' : '';
        this.isSafari = configService.browser.safariVersion > 0;
        // get auth state
        void userAuthService.getAuthToken();
    }

    async ngOnInit(): Promise<void> {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                switchMap(() => this.getChild(this.activatedRoute).data)
            )
            .subscribe((data) => {
                const title = data.title as string | undefined;
                const componentTitle = data.componentTitle as
                    | string
                    | undefined;
                const resultTitle: string[] = [];
                if (title) {
                    resultTitle.push(title);
                    resultTitle.push('-');
                }
                if (componentTitle) {
                    resultTitle.push(componentTitle);
                    resultTitle.push('-');
                }
                if (this.configService.isIndian) {
                    resultTitle.push('Indian Motorcycle');
                }
                if (this.configService.isSlingshot) {
                    resultTitle.push('Slingshot');
                }
                resultTitle.push('RIDE COMMAND®');
                this.titleService.setTitle(resultTitle.join(' '));

                const category = componentTitle ?? title;
                if (category) {
                    this.analyticsService.setCategory(category);
                }
            });

        // force load user profile to load email as soon as possible
        const profile = await firstValueFrom(
            this.profileService.getUserProfile$()
        );
        this.userStoreService.setUserPrefix(profile?.email || '');
        this.rideTypeService.setRideType(
            this.userStoreService.get('currentRideType') || 'ORV'
        );
    }

    sidenavStateChange(): void {
        this.store.dispatch(changeSidenavState());
    }

    getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
        if (activatedRoute.firstChild) {
            return this.getChild(activatedRoute.firstChild);
        } else {
            return activatedRoute;
        }
    }
}
